import { setup } from 'xstate';
import { Booking_Status_Enum, User_Roles_Enum } from '../state/gql/_types';
import type {
    BookingStatusContext,
    BookingStatusEvent,
    BookingStatusInput,
    UpdateBookingFn,
} from './bookingStatus.types';
import { hasPermission } from './bookingStatus.types';

/**
 * Guards for the booking status state machine.
 *
 * Permission logic: both ROLE_PERMISSIONS and TYPE_PERMISSIONS must allow the event.
 * Superadmin bypasses type restrictions. See hasPermission() in bookingStatus.types.
 */
const guards = {
    /**
     * Generic permission guard — checks hasPermission(role, type, event).
     * Used by all transitions that only need a permission check.
     */
    hasEventPermission: ({ context, event }: { context: BookingStatusContext; event: BookingStatusEvent }) => {
        if (!context.currentUser) return false;
        return hasPermission(context.currentUser.role, context.currentUser.type, event.type);
    },

    /**
     * OFFER guard: permission + booking must have required fields.
     */
    canOfferBooking: ({ context, event }: { context: BookingStatusContext; event: BookingStatusEvent }) => {
        if (!context.currentUser) return false;
        if (!hasPermission(context.currentUser.role, context.currentUser.type, event.type)) return false;

        if (!context.currentBooking) return false;
        const booking = context.currentBooking;
        return !!(booking.venue_id && booking.area_id && booking.talent_id && booking.date);
    },

    /**
     * ACCEPT/DECLINE guard: permission + user must be the assigned talent.
     * Superadmin bypasses the talent identity check.
     */
    canRespondToOffer: ({ context, event }: { context: BookingStatusContext; event: BookingStatusEvent }) => {
        if (!context.currentUser || !context.currentBooking) return false;
        if (!hasPermission(context.currentUser.role, context.currentUser.type, event.type)) return false;

        if (context.currentUser.role === User_Roles_Enum.Superadmin) return true;
        return context.currentUser.id === context.currentBooking.talent_id;
    },

    /**
     * TALENT_CANCEL guard: permission + user must be the assigned talent.
     * Superadmin bypasses the talent identity check.
     */
    canTalentCancel: ({ context, event }: { context: BookingStatusContext; event: BookingStatusEvent }) => {
        if (!context.currentUser || !context.currentBooking) return false;
        if (!hasPermission(context.currentUser.role, context.currentUser.type, event.type)) return false;

        if (context.currentUser.role === User_Roles_Enum.Superadmin) return true;
        return context.currentUser.id === context.currentBooking.talent_id;
    },
};

/**
 * Create the booking status state machine
 */
export function createBookingStatusMachine(updateBooking: UpdateBookingFn) {
    return setup({
        types: {
            context: {} as BookingStatusContext,
            events: {} as BookingStatusEvent,
            input: {} as BookingStatusInput,
        },
        guards,
        actions: {
            /**
             * Action to update booking status in the database
             */
            updateBookingStatus: async ({ context }: { context: BookingStatusContext }, params: { newStatus: Booking_Status_Enum }) => {
                if (!context.currentBooking) {
                    throw new Error('No booking to update');
                }

                const updatedBooking = await updateBooking({
                    id: context.currentBooking.id,
                    _set: { status: params.newStatus },
                });

                if (!updatedBooking) {
                    throw new Error('Failed to update booking');
                }

                // Update context with new booking data
                context.currentBooking = updatedBooking;
            },
        },
    }).createMachine({
        id: 'bookingStatus',

        context: ({ input }) => ({
            currentUser: input.currentUser,
            currentBooking: input.currentBooking,
        }),

        initial: 'getInitialState', // Use a transition to determine initial state

        states: {
            // Transient state to determine the correct initial state based on booking status
            getInitialState: {
                always: [
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Offered,
                        target: 'offered',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Accepted,
                        target: 'accepted',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Declined,
                        target: 'declined',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Cancelled,
                        target: 'cancelled',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.TalentCancelled,
                        target: 'talent_cancelled',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.CancellationApproved,
                        target: 'cancellation_approved',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.CancellationDenied,
                        target: 'cancellation_denied',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Noshow,
                        target: 'noshow',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.VenueCancelled,
                        target: 'venue_cancelled',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.BookerAdvancing,
                        target: 'booker_advancing',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.TalentAdvancing,
                        target: 'talent_advancing',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Advanced,
                        target: 'advanced',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Confirmed,
                        target: 'confirmed',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Active,
                        target: 'active',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Completed,
                        target: 'completed',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Cancellation,
                        target: 'cancellation',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Invoiced,
                        target: 'invoiced',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.Paid,
                        target: 'paid',
                    },
                    {
                        guard: ({ context }) => context.currentBooking?.status === Booking_Status_Enum.PaidOut,
                        target: 'paid_out',
                    },
                    // Default to draft if no status or status is draft
                    {
                        target: 'draft',
                    },
                ],
            },

            draft: {
                on: {
                    OFFER: {
                        target: 'offered',
                        guard: 'canOfferBooking',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Offered },
                        },
                    },
                    CANCEL: {
                        target: 'cancelled',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Cancelled },
                        },
                    },
                },
            },

            offered: {
                on: {
                    DECLINE: {
                        target: 'declined',
                        guard: 'canRespondToOffer',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Declined },
                        },
                    },
                    ACCEPT: {
                        target: 'accepted',
                        guard: 'canRespondToOffer',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Accepted },
                        },
                    },
                },
            },

            declined: {
                on: {
                    REASSIGN: {
                        target: 'draft',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Draft },
                        },
                    },
                },
            },

            accepted: {
                on: {
                    TALENT_CANCEL: {
                        target: 'talent_cancelled',
                        guard: 'canTalentCancel',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.TalentCancelled },
                        },
                    },
                    VENUE_CANCEL: {
                        target: 'venue_cancelled',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.VenueCancelled },
                        },
                    },
                    BOOKER_ADVANCE: {
                        target: 'booker_advancing',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.BookerAdvancing },
                        },
                    },
                },
            },

            booker_advancing: {
                on: {
                    TALENT_CANCEL: {
                        target: 'talent_cancelled',
                        guard: 'canTalentCancel',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.TalentCancelled },
                        },
                    },
                    VENUE_CANCEL: {
                        target: 'venue_cancelled',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.VenueCancelled },
                        },
                    },
                    BOOKER_ADVANCE_TO_TALENT: {
                        target: 'talent_advancing',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.TalentAdvancing },
                        },
                    },
                },
            },

            talent_advancing: {
                on: {
                    TALENT_CANCEL: {
                        target: 'talent_cancelled',
                        guard: 'canTalentCancel',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.TalentCancelled },
                        },
                    },
                    VENUE_CANCEL: {
                        target: 'venue_cancelled',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.VenueCancelled },
                        },
                    },
                    TALENT_ADVANCE: {
                        target: 'advanced',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Advanced },
                        },
                    },
                },
            },

            talent_cancelled: {
                on: {
                    APPROVE: {
                        target: 'cancellation_approved',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.CancellationApproved },
                        },
                    },
                    DENY: {
                        target: 'cancellation_denied',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.CancellationDenied },
                        },
                    },
                },
            },

            cancellation_approved: {
                on: {
                    REASSIGN: {
                        target: 'draft',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Draft },
                        },
                    },
                },
            },

            cancellation_denied: {
                on: {
                    WILL_NOSHOW: {
                        target: 'noshow',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Noshow },
                        },
                    },
                },
            },

            noshow: {
                on: {
                    REASSIGN: {
                        target: 'draft',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Draft },
                        },
                    },
                },
            },

            venue_cancelled: {
                on: {
                    CONFIRM: {
                        target: 'cancellation',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Cancellation },
                        },
                    },
                    INVOICE: {
                        target: 'invoiced',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Invoiced },
                        },
                    },
                },
            },

            advanced: {
                on: {
                    TALENT_CANCEL: {
                        target: 'talent_cancelled',
                        guard: 'canTalentCancel',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.TalentCancelled },
                        },
                    },
                    VENUE_CANCEL: {
                        target: 'venue_cancelled',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.VenueCancelled },
                        },
                    },
                    RECONFIRM_BOOKING: {
                        target: 'confirmed',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Confirmed },
                        },
                    },
                },
            },

            confirmed: {
                on: {
                    START: {
                        target: 'active',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Active },
                        },
                    },
                    TALENT_CANCEL: {
                        target: 'talent_cancelled',
                        guard: 'canTalentCancel',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.TalentCancelled },
                        },
                    },
                    VENUE_CANCEL: {
                        target: 'venue_cancelled',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.VenueCancelled },
                        },
                    },
                },
            },

            active: {
                on: {
                    COMPLETE: {
                        target: 'completed',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Completed },
                        },
                    },
                },
            },

            completed: {
                on: {
                    INVOICE: {
                        target: 'invoiced',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Invoiced },
                        },
                    },
                },
            },

            invoiced: {
                on: {
                    PAY: {
                        target: 'paid',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.Paid },
                        },
                    },
                },
            },

            paid: {
                on: {
                    PAY_OUT: {
                        target: 'paid_out',
                        guard: 'hasEventPermission',
                        actions: {
                            type: 'updateBookingStatus',
                            params: { newStatus: Booking_Status_Enum.PaidOut },
                        },
                    },
                },
            },

            // Final states
            cancelled: {
                type: 'final',
            },

            cancellation: {
                type: 'final',
            },

            paid_out: {
                type: 'final',
            },
        },
    });
}
